import React from 'react';
import { Container, Typography, Box, Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  LocalShipping,
  VerifiedUser,
  AttachMoney,
  Inventory,
  SupportAgent,
  AutoAwesome,
  Storefront,
  Discount,
} from '@mui/icons-material';

const AmazonDropshippingSuppliers = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Page Title */}
        <Typography variant="h3" gutterBottom align="center" className="text-main-600 font-bold">
          Our Trusted Supplier Network
        </Typography>
        <Typography variant="body1" paragraph align="center">
          We have established direct agreements with top-tier suppliers to ensure seamless fulfillment, high-quality products, and competitive pricing for our Amazon dropshipping business. Our trusted partners are committed to providing reliable service and efficient logistics, allowing you to focus on growing your business.
        </Typography>

        {/* Supplier Partners Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Our Official Supplier Partners
        </Typography>
        <Grid container spacing={4}>
          {suppliers.map((supplier, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {supplier.name}
                </Typography>
                <List>
                  {supplier.features.map((feature, i) => (
                    <ListItem key={i} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                        <AutoAwesome fontSize="small" sx={{ color: 'text-main-600' }} />
                      </ListItemIcon>
                      <ListItemText primary={feature} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Why Partner with Our Suppliers Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Why Partner with Our Suppliers?
        </Typography>
        <Grid container spacing={4}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Box sx={{ fontSize: '3rem', color: 'text-main-600' }}>
                  {benefit.icon}
                </Box>
                <Typography variant="h6" gutterBottom className="text-main-600 font-bold">
                  {benefit.title}
                </Typography>
                <Typography variant="body1">{benefit.description}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Efficient Multi-Supplier Management Section */}
        <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
          Efficient Multi-Supplier Management
        </Typography>
        <Typography variant="body1" paragraph>
          We handle multi-supplier integration to ensure:
        </Typography>
        <List>
          {managementFeatures.map((feature, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 30 }}>
                <VerifiedUser fontSize="small" sx={{ color: 'text-main-600' }} />
              </ListItemIcon>
              <ListItemText primary={feature} />
            </ListItem>
          ))}
        </List>

        {/* Call to Action Section */}
        <Box sx={{ mt: 6, textAlign: 'center', p: 4, borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom className="text-main-600 font-bold">
            Start Selling with Confidence!
          </Typography>
          <Typography variant="body1" paragraph>
            With our pre-established supplier agreements, you can trust that every order is processed smoothly and efficiently. Leverage our network to grow your Amazon dropshipping business and provide customers with the best shopping experience.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
            For supplier-related inquiries or assistance, contact our dedicated support team today!
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

// Supplier Data
const suppliers = [
  {
    name: 'AliExpress',
    features: [
      'Vast selection of products across multiple categories',
      'Competitive pricing and direct-to-consumer shipping',
      'Integrated with automation tools like DSers and AutoDS',
    ],
  },
  {
    name: 'CJ Dropshipping',
    features: [
      'Fast shipping times, including U.S. warehouse options',
      'Print-on-demand and branding services available',
      'Bulk order fulfillment and real-time order tracking',
    ],
  },
  {
    name: 'SaleHoo',
    features: [
      'Verified supplier directory with over 8,000 wholesalers and manufacturers',
      'Reliable U.S., U.K., and global suppliers',
      'Access to in-depth market research tools',
    ],
  },
  {
    name: 'Spocket',
    features: [
      'Focus on U.S. and EU-based suppliers for faster shipping',
      'High-quality products with private label options',
      'Automated fulfillment and seamless integration with Amazon',
    ],
  },
  {
    name: 'Wholesale2B',
    features: [
      'Over 1 million dropship-ready products',
      'Automated Amazon integration for easy product import',
      'No need to handle inventory or fulfillment',
    ],
  },
  {
    name: 'Modalyst',
    features: [
      'Focus on premium and branded products',
      'Faster delivery options with U.S. and EU suppliers',
      'Integrated automation for streamlined operations',
    ],
  },
  {
    name: 'Printful (For Print-on-Demand)',
    features: [
      'Custom branding and white-label product options',
      'No upfront inventory costs',
      'High-quality printing and fast global shipping',
    ],
  },
  {
    name: 'Doba',
    features: [
      'Centralized platform for managing multiple suppliers',
      'Bulk product sourcing and order fulfillment automation',
      'Reliable U.S.-based suppliers with fast shipping',
    ],
  },
];

// Benefits Data
const benefits = [
  {
    icon: <LocalShipping sx={{ color: 'text-main-600' }} />,
    title: 'Faster Shipping',
    description: 'Meet Amazon’s customer expectations with quick and reliable delivery.',
  },
  {
    icon: <AttachMoney sx={{ color: 'text-main-600' }} />,
    title: 'Exclusive Pricing',
    description: 'Maximize profit margins with exclusive discounts and competitive pricing.',
  },
  {
    icon: <VerifiedUser sx={{ color: 'text-main-600' }} />,
    title: 'Premium Quality',
    description: 'Access high-quality products from verified suppliers.',
  },
  {
    icon: <SupportAgent sx={{ color: 'text-main-600' }} />,
    title: 'Dedicated Support',
    description: 'Get assistance from our dedicated support team for seamless operations.',
  },
  {
    icon: <Inventory sx={{ color: 'text-main-600' }} />,
    title: 'Automated Fulfillment',
    description: 'Streamline your business with automated tools and integrations.',
  },
  {
    icon: <Discount sx={{ color: 'text-main-600' }} />,
    title: 'Competitive Pricing',
    description: 'Optimize sales with competitive pricing strategies.',
  },
];

// Management Features Data
const managementFeatures = [
  'Product availability and inventory consistency',
  'Diversification to prevent stock shortages',
  'Competitive pricing strategies for optimized sales',
  'Automated tools for simplified supplier coordination',
];

export default AmazonDropshippingSuppliers;